import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Crosshair, Map as MapIcon, MapPin, X } from 'lucide-react';
import { Button, Spinner } from '@parsina/framework';
import { C, RADIUS, soft } from '@parsina/framework/tokens';
import { MAP_TILE_URL } from '../../../lib/runtimeEnv';
import { loadLeaflet, currentPosition } from '../../../lib/leaflet';
import { useMyPageStrings } from '../myPageStrings';

// ─────────────────────────────────────────────────────────────────────────────
// «موقعیت روی نقشه» — the office pin, chosen on a map instead of typed as
// latitude/longitude.
//
//   • The map opens on a click, never on its own: «صفحهٔ من» shows every section
//     at once, and a location prompt on page load would be a question nobody
//     asked for.
//   • With no point saved, opening the map asks for the doctor's location and
//     drops the pin THERE as the default — then says so, because a doctor
//     filling this in at home must move it onto the office.
//   • Tap the map or drag the pin to move it. «موقعیت فعلی من» works even when
//     the map cannot load (it needs no tiles and no CDN).
//
// Every change goes through `onChange({lat, lng} | null)` into the office
// card's draft; nothing is saved until the card's own «ذخیره».
// ─────────────────────────────────────────────────────────────────────────────

/** Where a map with nothing better to show opens: Tehran. */
export const FALLBACK_CENTER = { lat: 35.6892, lng: 51.389 };
const ZOOM_POINT = 16;
const ZOOM_CITY = 11;

/**
 * Where a map with no point opens: `openAt` — `[lat, lng, zoom]`, the doctor's
 * country's view (PM-0218, lib/countryDefaults.js) — else Tehran at city zoom,
 * as before. A point the doctor chose always wins over either.
 */
export function emptyView(openAt) {
  if (Array.isArray(openAt) && openAt.length === 3 && openAt.every(Number.isFinite)) {
    return { at: { lat: openAt[0], lng: openAt[1] }, zoom: openAt[2] };
  }
  return { at: FALLBACK_CENTER, zoom: ZOOM_CITY };
}

const round6 = (n) => Math.round(Number(n) * 1e6) / 1e6;
/** A Leaflet LatLng (or anything with lat/lng) → the 6-decimal point we store. */
export const toPoint = (ll) => ({ lat: round6(ll.lat), lng: round6(ll.lng) });

const GEO_MESSAGE = {
  denied: 'myPage.office.map.denied',
  unavailable: 'myPage.office.map.unavailable',
  timeout: 'myPage.office.map.timeout',
  unsupported: 'myPage.office.map.unsupported',
};

const box = { display: 'grid', gap: '0.625rem' };
const row = { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem' };

/** The Leaflet surface. Mounted only while the map is open. */
function MapSurface({ value, openAt, onPoint, onReady, onFailed }) {
  const s = useMyPageStrings();
  const hostRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const onPointRef = useRef(onPoint);
  onPointRef.current = onPoint;
  const [state, setState] = useState('loading'); // loading | ready | failed

  const placeMarker = useCallback((L, at) => {
    if (markerRef.current) { markerRef.current.setLatLng([at.lat, at.lng]); return; }
    const marker = L.marker([at.lat, at.lng], { draggable: true, autoPan: true });
    marker.on('dragend', () => onPointRef.current?.(toPoint(marker.getLatLng())));
    marker.addTo(mapRef.current);
    markerRef.current = marker;
  }, []);

  useEffect(() => {
    let cancelled = false;
    loadLeaflet()
      .then((L) => {
        if (cancelled || !hostRef.current || mapRef.current) return;
        const empty = emptyView(openAt);
        const at = value || empty.at;
        const map = L.map(hostRef.current, { zoomControl: true }).setView([at.lat, at.lng], value ? ZOOM_POINT : empty.zoom);
        // A plain credit line: Leaflet's default prefix carries a flag icon,
        // and the map is about the doctor's office, not anything else.
        map.attributionControl?.setPrefix('<a href="https://leafletjs.com" target="_blank" rel="noopener noreferrer">Leaflet</a>');
        L.tileLayer(MAP_TILE_URL, { attribution: '© OpenStreetMap', maxZoom: 19 }).addTo(map);
        map.on('click', (e) => onPointRef.current?.(toPoint(e.latlng)));
        mapRef.current = map;
        if (value) placeMarker(L, value);
        setState('ready');
        onReady?.();
      })
      .catch(() => {
        if (cancelled) return;
        setState('failed');
        onFailed?.();
      });
    return () => {
      cancelled = true;
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; markerRef.current = null; }
    };
    // Built once per open; later points arrive through the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Follow the point however it changed — a tap, a drag, «موقعیت فعلی من», a
  // reset of the card — and drop the pin when it is cleared.
  // Keyed on the numbers, not the object: the editor builds a fresh point on
  // every render, and a keystroke in «نشانی» must not touch the map.
  const lat = value?.lat;
  const lng = value?.lng;
  useEffect(() => {
    const map = mapRef.current;
    const L = typeof window !== 'undefined' ? window.L : null;
    if (!map || !L) return;
    if (lat == null || lng == null) {
      if (markerRef.current) { markerRef.current.remove(); markerRef.current = null; }
      return;
    }
    const moved = !markerRef.current;
    placeMarker(L, { lat, lng });
    // Re-centre only when the pin is new or has left the visible area: a tap
    // must not yank the map out from under the finger that made it.
    if (moved || !map.getBounds?.().contains?.([lat, lng])) {
      map.setView([lat, lng], Math.max(map.getZoom?.() || 0, ZOOM_POINT));
    }
  }, [lat, lng, state, placeMarker]);

  if (state === 'failed') {
    return (
      <div
        data-testid="my-page-office-map-failed"
        style={{
          padding: '1rem', borderRadius: RADIUS.control, border: `1px dashed ${C.border}`,
          background: C.bg, color: C.textSecondary, fontSize: '0.8125rem', textAlign: 'center',
        }}
      >
        {s('myPage.office.map.failed')}
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', borderRadius: RADIUS.control, overflow: 'hidden', border: `1px solid ${C.border}` }}>
      {/* Leaflet lays its panes out in LTR; an RTL page would mirror its controls. */}
      <div
        ref={hostRef}
        dir="ltr"
        role="application"
        aria-label={s('myPage.office.map.label')}
        data-testid="my-page-office-map"
        style={{ height: 'clamp(16rem, 42vh, 22rem)', width: '100%', background: C.bg }}
      />
      {state === 'loading' ? (
        <div
          role="status"
          style={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
            background: soft(C.card, 0.8), color: C.navy, fontSize: '0.8125rem', fontWeight: 600,
          }}
        >
          <Spinner size={16} /> {s('myPage.office.map.loading')}
        </div>
      ) : null}
    </div>
  );
}

// `openAt`: `[lat, lng, zoom]` for a map with no point yet — see `emptyView`.
export default function OfficeMap({ value, onChange, disabled = false, openAt }) {
  const s = useMyPageStrings();
  const [open, setOpen] = useState(false);
  const [locating, setLocating] = useState(false);
  const [message, setMessage] = useState(null); // { tone: 'info' | 'warn', key }
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const locate = useCallback(async () => {
    setLocating(true);
    setMessage({ tone: 'info', key: 'myPage.office.map.locating' });
    try {
      const point = toPoint(await currentPosition());
      if (!alive.current) return;
      onChange(point);
      setMessage({ tone: 'info', key: 'myPage.office.map.found' });
    } catch (err) {
      if (alive.current) setMessage({ tone: 'warn', key: GEO_MESSAGE[err?.message] || GEO_MESSAGE.unavailable });
    } finally {
      if (alive.current) setLocating(false);
    }
  }, [onChange]);

  const openMap = () => {
    setOpen(true);
    // The doctor's own position is the default — but never over a point they already chose.
    if (!value) locate();
    else setMessage({ tone: 'info', key: 'myPage.office.map.hint' });
  };

  const pick = useCallback((point) => {
    onChange(point);
    setMessage({ tone: 'info', key: 'myPage.office.map.hint' });
  }, [onChange]);

  return (
    <div style={box} data-testid="my-page-office-geo">
      <div style={row}>
        {!open ? (
          <Button variant={value ? 'outline' : 'default'} size="sm" disabled={disabled} onClick={openMap} data-testid="my-page-office-map-open">
            <MapIcon size={16} aria-hidden /> {value ? s('myPage.office.map.change') : s('myPage.office.map.pick')}
          </Button>
        ) : (
          <Button variant="outline" size="sm" onClick={() => setOpen(false)} data-testid="my-page-office-map-close">
            <X size={16} aria-hidden /> {s('myPage.office.map.close')}
          </Button>
        )}
        <Button variant="ghost" size="sm" disabled={disabled || locating} onClick={locate} data-testid="my-page-office-map-locate">
          {locating ? <Spinner size={14} /> : <Crosshair size={16} aria-hidden />} {s('myPage.office.map.myLocation')}
        </Button>
        {value && !disabled ? (
          <Button
            variant="ghost" size="sm"
            onClick={() => { onChange(null); setMessage(null); }}
            data-testid="my-page-office-map-clear"
          >
            {s('myPage.office.map.clear')}
          </Button>
        ) : null}
      </div>

      {value && !open ? (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.8125rem', color: C.textPrimary }} data-testid="my-page-office-map-set">
          <MapPin size={16} aria-hidden style={{ color: C.green, flexShrink: 0 }} />
          {s('myPage.office.map.isSet')}
        </div>
      ) : null}

      {open ? (
        <MapSurface
          value={value}
          openAt={openAt}
          onPoint={pick}
          onFailed={() => setMessage({ tone: 'warn', key: 'myPage.office.map.failed' })}
        />
      ) : null}

      {message ? (
        <div
          role="status"
          aria-live="polite"
          data-testid="my-page-office-map-message"
          data-tone={message.tone}
          style={{ fontSize: '0.75rem', color: message.tone === 'warn' ? C.amber : C.textSecondary }}
        >
          {s(message.key)}
        </div>
      ) : null}
    </div>
  );
}
